// copy of the connector and account installers in
// packages/integrations/databricks/frontend/src/index.tsx, without the
// account-admin switch
import { DOCS_URLS } from "@p0security/frontend-core";
import { VALID_AWS_REGIONS } from "@p0security/integrations-aws-frontend";
import {
  GOVCLOUD_REGIONS,
  P0_DEFAULT_AWS_REGION,
} from "@p0security/integrations-aws-shared/constants";
import { DATABRICKS_AI_INTEGRATION_KEY } from "@p0security/integrations-databricks-ai-shared/constants";
import type { DatabricksAiComponents } from "@p0security/integrations-databricks-ai-shared/types";
import {
  type FrontendInstaller,
  integrationOptionProvider,
} from "@p0security/integrations-installer";
import { installedIds } from "@p0security/shared/install/installed";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import { Typography } from "@p0security/ui";

import {
  PROVIDERS_TERRAFORM,
  renderAccountTerraform,
  renderConnectorTerraform,
  toApplicationIdOutput,
  toMetastoreAdminGroupsVariable,
} from "./iac.ts";
import { listInstalledKeys } from "./options.ts";

const { Link, Paragraph, Text } = Typography;

type DatabricksAiInstaller =
  FrontendInstaller<DatabricksAiComponents>["components"];

// The connector's ARN is built in the `aws` partition, so GovCloud regions
// can't host it. P0's default region comes first, as the preselected one.
const CONNECTOR_REGIONS = [
  P0_DEFAULT_AWS_REGION,
  ...VALID_AWS_REGIONS.filter(
    (region) =>
      region !== P0_DEFAULT_AWS_REGION && !GOVCLOUD_REGIONS.includes(region)
  ),
];

/** The command that prints the account's service principal application ID. */
const ApplicationIdCommand = ({ accountId }: { accountId: string }) => (
  <Text code>terraform output -raw {toApplicationIdOutput(accountId)}</Text>
);

const connectorInstaller: DatabricksAiInstaller["connector"] = {
  instructions: (context, awsAccountId, item) => ({
    commands: {
      iac: [
        {
          command: PROVIDERS_TERRAFORM,
          header: (
            <Paragraph>
              Your Terraform configuration needs these providers. If it already
              has a <Text code>required_providers</Text> block, add them to it.
              If a module in it pins the AWS provider to 5.x, as P0&apos;s RDS
              security perimeter and Postgres or MySQL connector modules do,
              apply this from a configuration of its own:
            </Paragraph>
          ),
        },
        {
          command: renderConnectorTerraform(context, awsAccountId, item),
          header: (
            <Paragraph>
              Then add the following, apply it as an AWS admin of account{" "}
              {awsAccountId}, and click &quot;Next&quot;.
            </Paragraph>
          ),
        },
      ],
    },
    help: (
      <Paragraph>
        P0 reaches Databricks through a connector Lambda in AWS account{" "}
        {awsAccountId}. The connector signs in to Databricks with its AWS
        identity, so no secret is stored anywhere. Its Terraform turns on
        outbound web identity federation for the whole AWS account, which lets
        it mint that identity.
      </Paragraph>
    ),
  }),
  items: {
    region: {
      optionProvider: async () => CONNECTOR_REGIONS.map((id) => ({ id })),
    },
  },
  optionProvider: integrationOptionProvider("connector", [
    { component: "iam-write", integration: "aws" },
  ]),
  // The connector's AWS account is chosen from those, so without one the
  // choice would be empty, with nothing to say why.
  prerequisiteMessages: async ({ installed: { aws } }) => {
    // An installed integration's config isn't typed by its components.
    if (!aws || !installedIds<string, NewConfig>("iam-write", aws).length)
      return (
        <div>
          Install P0&apos;s AWS IAM management for the connector&apos;s AWS
          account first: P0 invokes the connector as its role.
        </div>
      );
  },
};

const accountInstaller: DatabricksAiInstaller["account"] = {
  instructions: (_context, accountId, item) => ({
    commands: {
      iac: [
        {
          command: renderAccountTerraform(accountId, item),
          header: (
            <Paragraph>
              Add the following to the Terraform configuration that holds the
              connector. Set{" "}
              <Text code>{toMetastoreAdminGroupsVariable(accountId)}</Text> to
              the display name of the admin group of each metastore in the
              account. Apply it as a Databricks account admin with AWS access to
              the connector&apos;s account. Then click &quot;Next&quot;, and
              enter the value that{" "}
              <ApplicationIdCommand accountId={accountId} /> prints.
            </Paragraph>
          ),
        },
      ],
    },
    help: (
      <>
        <Paragraph>
          P0 manages Databricks account {accountId} through a service principal
          there, which the connector signs in as with its AWS identity. For each
          agent&apos;s request, P0 creates a service principal of its own,
          grants it what the request asks for, and deletes it when the access
          ends.
        </Paragraph>
        <Paragraph>
          P0&apos;s principal is account admin, so that it can create those
          service principals and find the account&apos;s workspaces. It is
          metastore admin of every metastore, as a member of each
          metastore&apos;s admin group, so that it can grant Unity Catalog
          privileges and hand what an agent created to the owner principal.
        </Paragraph>
        <Paragraph>
          P0 also makes its principal workspace admin of each workspace an agent
          lists or requests, and of the account&apos;s lowest-ID workspace when
          the account is saved, where it checks for the Unity Gateway beta. P0
          never undoes it.
        </Paragraph>
      </>
    ),
  }),
  items: {
    applicationId: {
      labeler: (_context, accountId) => (
        <>
          The service principal&apos;s application ID. Run{" "}
          <ApplicationIdCommand accountId={accountId} /> where you applied the
          account&apos;s Terraform.
        </>
      ),
    },
    connector: {
      optionProvider: integrationOptionProvider("account", [
        { component: "connector", integration: DATABRICKS_AI_INTEGRATION_KEY },
      ]),
    },
  },
  prerequisiteMessages: async (context) => {
    if (!listInstalledKeys(context, "connector").size)
      return (
        <div>
          Install the Databricks (AI) connector first: P0 reaches each account
          through it.
        </div>
      );
  },
};

export const databricksAiInstaller: FrontendInstaller<DatabricksAiComponents> =
  {
    components: {
      account: accountInstaller,
      connector: connectorInstaller,
    },
    preamble: (
      <Paragraph>
        Install the connector first, then each Databricks account that P0 should
        manage. See the{" "}
        <Link
          href={DOCS_URLS.DATABRICKS_AI_INSTALL}
          rel="noopener noreferrer"
          target="_blank"
        >
          installation guide
        </Link>{" "}
        for the access and tools each step needs.
      </Paragraph>
    ),
  };
